/* eslint-disable react/no-unescaped-entities */
/**
 * /reseau-specialistes — Réseau des spécialistes CONFILEX.
 *
 * 2 sections strictement séparées :
 *  A. Experts partenaires CONFILEX — salons premium, priorité absolue.
 *  B. Avocats référencés au barreau — fiches simples (source data.gouv.fr / CNB).
 *
 * Philosophie confirmée par le fondateur (fév. 2026) :
 *  - les avocats non partenaires ne sont JAMAIS présentés avec un badge, un
 *    rating, une photo ou un CTA commercial ;
 *  - la source officielle est toujours mentionnée ;
 *  - les partenaires sont TOUJOURS affichés en premier, même si un seul.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, MapPin, Scale, ArrowRight, BadgeCheck, Building2, Lock, UserX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb } from "@/components/Seo";
import { apiClient } from "@/lib/api";

const TOP_BARREAUX = [
  "PARIS", "LYON", "MARSEILLE", "HAUTS-DE-SEINE", "BORDEAUX",
  "TOULOUSE", "LILLE", "MONTPELLIER", "NANTES", "NICE",
];

const TOP_SPECIALITES = [
  "Droit des affaires", "Droit du travail", "Droit de la famille",
  "Droit immobilier", "Droit fiscal", "Droit pénal",
  "Droit des sociétés", "Droit social",
];

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function ReseauSpecialistes() {
  const [q, setQ] = useState("");
  const [barreau, setBarreau] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [partners, setPartners] = useState([]);
  const [publics, setPublics] = useState([]);
  const [sourceUpdated, setSourceUpdated] = useState(null);
  const [loading, setLoading] = useState(false);

  const debouncedQ = useDebounced(q);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // A. Partenaires — endpoint experts (liste publique + filtrage local)
        const partnersRes = await apiClient.get(`/experts`).catch(() => ({ data: [] }));
        let allPartners = partnersRes?.data || [];
        // Filtrage local sur q / spécialité (l'endpoint n'expose pas de filtre serveur)
        if (debouncedQ) {
          const q = debouncedQ.toLowerCase();
          allPartners = allPartners.filter((p) =>
            (`${p.first_name} ${p.last_name} ${p.city || ""} ${p.company || ""}`).toLowerCase().includes(q)
          );
        }
        if (specialite) {
          const s = specialite.toLowerCase();
          allPartners = allPartners.filter((p) =>
            (p.specialties || []).some((x) => (x || "").toLowerCase().includes(s)) ||
            (p.profession || "").toLowerCase().includes(s)
          );
        }

        // B. Résultats publics — endpoint lawyers-directory
        const p2 = new URLSearchParams();
        if (debouncedQ) p2.set("q", debouncedQ);
        if (barreau) p2.set("barreau", barreau);
        if (specialite) p2.set("specialty", specialite);
        p2.set("limit", "24");
        const publicsRes = await apiClient.get(`/lawyers-directory/search?${p2}`).catch(() => ({ data: { items: [] } }));

        if (cancelled) return;
        setPartners(allPartners);
        setPublics(publicsRes?.data?.items || []);
        setSourceUpdated(publicsRes?.data?.source_updated_at || "2026-02-05");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [debouncedQ, barreau, specialite]);

  const hasActiveFilter = useMemo(
    () => Boolean(debouncedQ || barreau || specialite),
    [debouncedQ, barreau, specialite]
  );

  return (
    <div className="bg-[#FAFAF7] min-h-screen" data-testid="page-reseau-specialistes">
      <Seo
        title="Réseau de spécialistes CONFILEX — Trouver un avocat, notaire ou expert-comptable"
        description="Consultez les experts partenaires CONFILEX (salons premium) et l'annuaire officiel des avocats référencés au barreau (source : Conseil national des barreaux · data.gouv.fr)."
        canonical="/reseau-specialistes"
        keywords={["réseau spécialistes", "avocat", "expert-comptable", "notaire", "annuaire avocats CNB", "barreau"]}
        jsonLd={[
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Réseau de spécialistes", url: "/reseau-specialistes" },
          ]),
        ]}
      />
      <Navbar variant="light" />
      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
        {/* En-tête */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">
            Réseau de spécialistes
          </div>
          <h1 className="font-serif text-3xl lg:text-5xl text-[#0B3A2A] mb-3 leading-tight">
            Trouver le professionnel adapté à votre dossier
          </h1>
          <p className="text-slate-600 max-w-2xl leading-relaxed">
            Les <strong>Experts partenaires CONFILEX</strong> disposent d'un salon
            professionnel complet et sont mis en avant en priorité. En complément,
            vous pouvez consulter l'annuaire officiel des avocats référencés au barreau.
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 lg:p-6 shadow-sm mb-10" data-testid="search-bar">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="relative md:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="input-name"
                placeholder="Nom, cabinet ou ville"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              data-testid="select-barreau"
              value={barreau}
              onChange={(e) => setBarreau(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Tous les barreaux</option>
              {TOP_BARREAUX.map((b) => <option key={b} value={b}>Barreau de {b}</option>)}
            </select>
            <select
              data-testid="select-specialite"
              value={specialite}
              onChange={(e) => setSpecialite(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Toutes les spécialités</option>
              {TOP_SPECIALITES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* SECTION A — Experts partenaires */}
        <section className="mb-14" data-testid="section-partners">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#0B3A2A]">
              Experts partenaires CONFILEX
            </h2>
            <span className="text-xs text-slate-500">
              {partners.length} salon{partners.length > 1 ? "s" : ""} disponible{partners.length > 1 ? "s" : ""}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-6 max-w-3xl">
            Ces professionnels ont adhéré à CONFILEX, validé leur compte et disposent
            d'un salon premium avec présentation, parcours, publications et modalités
            de contact.
          </p>

          {partners.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
              Aucun expert partenaire ne correspond à cette recherche pour l'instant.
              {hasActiveFilter && <> Essayez de retirer un filtre ou consultez la section publique ci-dessous.</>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.slice(0, 12).map((p) => (
                <Link
                  key={p.id}
                  to={`/expert/${p.id}`}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-[#C9A961]/60 transition"
                  data-testid="card-partner"
                >
                  {p.photo && (
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      <img src={p.photo} alt={`${p.first_name} ${p.last_name}`}
                           className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start gap-2 mb-1">
                      <BadgeCheck className="w-4 h-4 text-[#C9A961] mt-1 shrink-0" />
                      <div>
                        <div className="font-serif text-lg text-[#0B3A2A] leading-tight">
                          {p.first_name} {p.last_name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{p.profession}</div>
                      </div>
                    </div>
                    {p.city && (
                      <div className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{p.city}
                      </div>
                    )}
                    <div className="mt-3 text-xs font-medium text-[#0B3A2A] group-hover:underline inline-flex items-center gap-1">
                      Découvrir son salon <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Séparateur institutionnel */}
        <div className="my-14 relative" data-testid="section-separator">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="px-4 bg-[#FAFAF7] text-xs text-slate-500 uppercase tracking-widest">
              Complément — annuaire officiel des avocats
            </div>
          </div>
        </div>

        {/* SECTION B — Avocats CNB */}
        <section className="mb-16" data-testid="section-public">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#0B3A2A]">
              Avocats référencés au barreau
            </h2>
            <span className="text-xs text-slate-500">
              {publics.length} résultat{publics.length > 1 ? "s" : ""} publi{publics.length > 1 ? "s" : "c"}
            </span>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 mb-6 text-xs text-slate-600 leading-relaxed max-w-3xl">
            <p className="mb-1.5">
              Recherche effectuée à partir de l'annuaire officiel des avocats publié par le
              Conseil national des barreaux sur data.gouv.fr — <strong>Licence Ouverte Etalab 2.0</strong>.
              {sourceUpdated && <> Dernière mise à jour de la source : {sourceUpdated}.</>}
            </p>
            <p className="mb-1.5">
              L'identité de ces avocats est <strong>masquée</strong>. Seules la spécialité et
              la localité sont visibles avant le déblocage individuel de la fiche.
              Ces avocats ne sont pas partenaires de CONFILEX ; leur classement n'est pas
              établi par la plateforme.
            </p>
            <p className="text-[11px] text-slate-500 italic">
              CONFILEX n'est ni partenaire ni agent du Conseil national des barreaux (CNB).
              Aucune notation, aucun classement subjectif, aucune comparaison n'est établie
              entre les avocats.{" "}
              <a href="/legal/definitions" className="text-[#0B3A2A] underline hover:no-underline">
                En savoir plus
              </a>
              .
            </p>
          </div>

          {loading && <div className="text-slate-400 text-sm">Recherche en cours…</div>}

          {!loading && publics.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">
              Aucun avocat ne correspond à ces critères. Essayez d'élargir la recherche.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {publics.map((l, idx) => {
                const isLocked = l.locked === true || l.unlocked === false;
                const fullName = l.fullName || `${l.prenom || ""} ${l.nom || ""}`.trim();
                const displayName = isLocked
                  ? `Avocat n°${String(idx + 1).padStart(3, "0")}`
                  : fullName;
                const cardId = l.anon_id || l.id;
                return (
                  <div
                    key={cardId}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C9A961]/50 hover:shadow-sm transition relative"
                    data-testid="card-public"
                  >
                    {isLocked && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        <Lock className="w-3 h-3" /> Identité masquée
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      {isLocked ? (
                        <UserX className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                      ) : (
                        <Scale className="w-4 h-4 text-[#0B3A2A] mt-1 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={`font-medium truncate ${isLocked ? "text-slate-500 italic" : "text-slate-800"}`}>
                          {displayName}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> Barreau de {l.barreau}
                        </div>
                        {l.city && (
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {l.city}
                            {!isLocked && l.postal_code && ` ${l.postal_code}`}
                          </div>
                        )}
                        {(l.specialties_officielles?.length > 0 || l.specialties?.length > 0) && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {(l.specialties_officielles || l.specialties || []).slice(0, 3).map((s) => (
                              <span key={s} className="text-[10px] bg-[#C9A961]/10 text-[#0B3A2A] px-2 py-0.5 rounded font-medium">
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                        {l.experience_years != null && (
                          <div className="text-xs text-slate-500 mt-2">
                            {l.experience_years} ans d'expérience
                          </div>
                        )}
                        {isLocked ? (
                          <>
                            <Link
                              to={`/lawyer/${cardId}`}
                              className="mt-4 w-full inline-flex items-center justify-center gap-1 text-xs font-medium bg-[#0B3A2A] text-white px-3 py-2 rounded-lg hover:bg-[#0B3A2A]/90 transition"
                              data-testid="btn-unlock"
                            >
                              <Lock className="w-3 h-3" /> Débloquer la fiche complète
                            </Link>
                            <p className="mt-2 text-[10px] text-slate-400 leading-tight">
                              Vous êtes cet avocat ?{" "}
                              <Link
                                to={`/claim-profile/${cardId}`}
                                className="text-[#0B3A2A] underline hover:no-underline"
                                data-testid="btn-claim-request"
                              >
                                Revendiquer ou corriger ma fiche
                              </Link>
                            </p>
                          </>
                        ) : (
                          <>
                            <Link
                              to={`/lawyer/${cardId}`}
                              className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#0B3A2A] hover:underline"
                            >
                              Consulter la fiche complète <ArrowRight className="w-3 h-3" />
                            </Link>
                            <p className="mt-2 text-[10px] text-slate-400 leading-tight">
                              Correction à signaler ?{" "}
                              <Link
                                to={`/claim-profile/${cardId}`}
                                className="text-[#0B3A2A] underline hover:no-underline"
                              >
                                Demander une modification
                              </Link>
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Bloc CTA */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0B3A2A] to-[#1a5240] p-8 text-white text-center">
          <h3 className="font-serif text-2xl mb-2">Vous ne trouvez pas le bon professionnel ?</h3>
          <p className="text-white/80 text-sm mb-5 max-w-xl mx-auto">
            Confiez votre dossier à CONFILEX : nous préparons votre analyse documentaire
            et orientons votre demande vers plusieurs profils qualifiés.
          </p>
          <Button asChild className="bg-[#C9A961] hover:bg-[#B8985A] text-[#0B3A2A]">
            <Link to="/dossier" data-testid="cta-dossier">Démarrer une orientation</Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
